import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useUnreadMessagesStore } from '../../store/unread-messages.store';
import { 
  Home, 
  Users, 
  User, 
  Shield, 
  Globe, 
  Grid3x3, 
  Key, 
  Settings, 
  Calendar, 
  Book, 
  DollarSign, 
  Heart, 
  BarChart3, 
  ChevronRight, 
  ChevronLeft, 
  ShoppingCart,
  Package,
  Lock,
  Banknote,
  FileText
} from 'lucide-react';

interface Permiso {
  id: string;
  nombre: string;
  descripcion: string;
}

interface Modulo {
  id: string;
  nombre: string;
  descripcion: string;
  ruta: string;
  icono: string;
  orden: number;
  tipo: 'navigation' | 'drawer';
  moduloPadreId?: string;
  permisos: Permiso[];
  submodulos?: Modulo[];
}

interface Rol {
  id: string;
  nombre: string;
  descripcion: string;
  modulos: Modulo[];
}

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  roles: Rol[];
}

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

// Mapeo de iconos usando Lucide React
const iconMap: Record<string, React.ComponentType<any>> = {
  dashboard: Home,
  users: Users,
  UsersIcon: Users,
  UserIcon: User,
  IdentificationIcon: User,
  GlobeAltIcon: Globe,
  ShieldCheckIcon: Shield,
  UserGroupIcon: Users,
  Squares2X2Icon: Grid3x3,
  KeyIcon: Key,
  "user-cog": Settings,
  shield: Shield,
  calendar: Calendar,
  book: Book,
  "dollar-sign": DollarSign,
  heart: Heart,
  "chart-bar": BarChart3,
  settings: Settings,
  ShoppingCart: ShoppingCart,
  Package: Package,
  BarChart3: BarChart3,
  barchart3: BarChart3,
  Lock: Lock,
  Banknote: Banknote,
  banknote: Banknote,
  FileText: FileText,
  filetext: FileText
};

export const Sidebar: React.FC<SidebarProps> = ({ 
  isCollapsed, 
  setIsCollapsed
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = usePermissions();
  const { totalNoLeidos } = useUnreadMessagesStore();
  
  // Simplificar: usar sessionStorage para persistir el estado entre re-renders
  const [activeDrawer, setActiveDrawerState] = React.useState<string | null>(() => {
    return sessionStorage.getItem('activeDrawer');
  });
  
  const setActiveDrawer = (drawerId: string | null) => {
    if (drawerId) {
      sessionStorage.setItem('activeDrawer', drawerId);
    } else {
      sessionStorage.removeItem('activeDrawer');
    }
    setActiveDrawerState(drawerId);
  };
  

  // Obtener módulos padre (ya vienen correctamente del backend con estructura jerárquica)
  const modulosPadre = React.useMemo(() => {
    if (!user?.roles) return [];

    const modulosMap = new Map<string, Modulo>();

    // Recopilar todos los módulos padre de todos los roles
    user.roles.forEach((rol: any) => {
      rol.modulos.forEach((modulo: any) => {
        if (modulosMap.has(modulo.id)) {
          // Si ya existe, combinar permisos únicos
          const existing = modulosMap.get(modulo.id)!;
          const permisosUnicos = new Map();
          
          [...existing.permisos, ...modulo.permisos].forEach(permiso => {
            permisosUnicos.set(permiso.id, permiso);
          });
          
          existing.permisos = Array.from(permisosUnicos.values());
        } else {
          // Copiar con submodulos incluidos
          modulosMap.set(modulo.id, { 
            ...modulo,
            submodulos: modulo.submodulos || []
          });
        }
      });
    });

    // Convertir a array y ordenar (solo módulos padre)
    return Array.from(modulosMap.values()).sort((a, b) => a.orden - b.orden);
  }, [user]);


  const handleModuleClick = (modulo: Modulo) => {
    // Para módulos tipo drawer, abrir drawer en lugar de navegar
    if (modulo.tipo === 'drawer') {
      const newActiveDrawer = activeDrawer === modulo.id ? null : modulo.id;
      setActiveDrawer(newActiveDrawer);
      return;
    }
    
    // Navegación normal para otros módulos
    if (modulo.ruta) {
      // Asegurar que la ruta tenga el prefijo /dashboard
      const rutaCompleta = modulo.ruta.startsWith('/dashboard') 
        ? modulo.ruta 
        : `/dashboard${modulo.ruta}`;
      navigate(rutaCompleta);
    }
  };



  // Alias de rutas: cuando un módulo/submódulo tiene una ruta legacy
  // pero el frontend ahora usa una ruta diferente
  const routeAliases: Record<string, string[]> = {
    '/dashboard/reportes': ['/dashboard/mi-farmacia/reportes'],
  };

  const isActiveRoute = (ruta: string) => {
    // Para la ruta raíz del dashboard, solo debe estar activa si estamos exactamente en /dashboard
    if (ruta === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Para otras rutas, verificar la ruta original y sus aliases
    const rutasAComprobar = [ruta, ...(routeAliases[ruta] || [])];
    return rutasAComprobar.some(r => location.pathname === r || location.pathname.startsWith(`${r}/`));
  };

  const isModuleActive = (modulo: Modulo) => {
    // Para módulos drawer, verificar si alguno de sus submódulos está activo
    if (modulo.tipo === 'drawer') {
      return (modulo.submodulos || []).some(submodulo => 
        isActiveRoute(`/dashboard${submodulo.ruta}`)
      );
    }
    // Para módulos normales, verificar la ruta con prefijo /dashboard
    const rutaCompleta = modulo.ruta?.startsWith('/dashboard') 
      ? modulo.ruta 
      : `/dashboard${modulo.ruta}`;
    return isActiveRoute(rutaCompleta);
  };

  // Determinar si un módulo (o alguno de sus submódulos) está relacionado con ventas/pedidos
  const esModuloVentas = (modulo: Modulo): boolean => {
    const rutasVentas = ['/ventas', '/pedidos'];
    const moduloRuta = modulo.ruta || '';
    if (rutasVentas.some(r => moduloRuta.includes(r))) return true;
    if (modulo.submodulos) {
      return modulo.submodulos.some(sub => {
        const subRuta = sub.ruta || '';
        return rutasVentas.some(r => subRuta.includes(r));
      });
    }
    return false;
  };

  return (
    <aside 
      className={`${
        isCollapsed ? 'w-20' : 'w-72'
      } relative bg-gradient-to-br from-white via-gray-50 to-white text-gray-900 flex flex-col transition-all duration-300 ease-in-out shadow-xl border-r border-gray-200 overflow-hidden`}
      style={{ height: 'calc(100vh - 5rem)' }}
    >

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto overflow-x-hidden">
        {modulosPadre
          .filter((modulo) => {
            // Para módulos drawer, verificar si tiene al menos un submódulo con permiso de ver
            if (modulo.tipo === 'drawer') {
              const tieneSubmodulosConPermisos = (modulo.submodulos || [])
                .some(submodulo => hasPermission(submodulo.nombre, 'ver'));
              return tieneSubmodulosConPermisos;
            }
            
            // Para módulos normales, verificar si tiene permiso de ver
            return hasPermission(modulo.nombre, 'ver');
          })
          .map((modulo) => {
          const isActive = isModuleActive(modulo);
          const isDrawerOpen = activeDrawer === modulo.id;
          const IconComponent = iconMap[modulo.icono] || Settings;
          
          return (
            <div key={modulo.id} className="relative group">
              <button
                onClick={() => handleModuleClick(modulo)}
                className={`w-full flex items-center rounded-xl transition-all duration-200 group/item ${
                  isCollapsed ? 'px-3 py-4 justify-center' : 'px-4 py-3'
                } ${
                  isActive 
                    ? 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white shadow-md border border-[#4CAF50]/20' 
                    : 'text-gray-700 bg-white hover:bg-[#4CAF50]/10 hover:text-[#2196F3] border border-gray-200 hover:border-[#4CAF50]/20 hover:shadow-sm'
                }`}
              >
                {/* Icono */}
                <div className={`flex-shrink-0 relative ${isCollapsed ? '' : 'mr-3'}`}>
                  <div className={`${isActive ? 'text-white' : 'text-[#4CAF50] group-hover/item:text-[#2196F3]'} transition-colors duration-200`}>
                    <IconComponent
                      className={`${isCollapsed ? 'h-6 w-6' : 'h-5 w-5'}`}
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                  </div>
                  
                  {/* Indicador de módulo activo */}
                  {isActive && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#4CAF50]/40 rounded-full ring-2 ring-white animate-pulse"></div>
                  )}

                  {/* Badge de mensajes no leídos (modo colapsado) */}
                  {isCollapsed && totalNoLeidos > 0 && esModuloVentas(modulo) && (
                    <div className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm">
                      {totalNoLeidos > 99 ? '99+' : totalNoLeidos}
                    </div>
                  )}
                </div>
                
                {/* Texto del módulo */}
                {!isCollapsed && (
                  <div className="flex-1 text-left">
                    <span className={`text-sm font-medium transition-all duration-200 ${isActive ? 'text-white' : 'text-gray-700 group-hover/item:text-[#2196F3]'}`}>
                      {modulo.nombre}
                    </span>
                    {modulo.descripcion && (
                      <p className={`text-xs mt-0.5 transition-all duration-200 ${isActive ? 'text-white/90' : 'text-gray-500 group-hover/item:text-[#4CAF50]'}`}>
                        {modulo.descripcion}
                      </p>
                    )}
                  </div>
                )}

                {/* Indicador de flecha para módulos drawer o indicador de activo */}
                {!isCollapsed && (
                  <>
                    {/* Badge de mensajes no leídos (modo expandido) */}
                    {totalNoLeidos > 0 && esModuloVentas(modulo) && (
                      <div className="flex-shrink-0 mr-2">
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full shadow-sm">
                          {totalNoLeidos > 99 ? '99+' : totalNoLeidos}
                        </span>
                      </div>
                    )}
                    {(modulo.tipo === 'drawer') ? (
                      <div className="flex-shrink-0">
                        <ChevronRight 
                          className={`w-4 h-4 transition-all duration-200 ${
                            isDrawerOpen ? 'rotate-90' : 'rotate-0'
                          } ${isActive ? 'text-white' : 'text-gray-400 group-hover/item:text-[#4CAF50]'}`}
                        />
                      </div>
                    ) : isActive && (
                      <div className="flex-shrink-0 w-2 h-2 bg-[#4CAF50]/40 rounded-full animate-pulse"></div>
                    )}
                  </>
                )}
              </button>

              {/* Mostrar submódulos como pestañas cuando el drawer está abierto */}
              {!isCollapsed && (
                <div 
                  className={`ml-4 mt-2 space-y-1 border-l-2 border-[#4CAF50]/20 pl-4 transition-all duration-300 ease-in-out overflow-hidden ${
                    isDrawerOpen 
                      ? 'max-h-96 opacity-100 transform translate-y-0' 
                      : 'max-h-0 opacity-0 transform -translate-y-2'
                  }`}
                >
                  {(modulo.submodulos || [])
                    .filter(submodulo => hasPermission(submodulo.nombre, 'ver'))
                    .filter(submodulo => submodulo.nombre !== 'Productos Vendidos')
                    .sort((a, b) => a.orden - b.orden)
                    .map((submodulo) => (
                      <button
                        key={submodulo.id}
                        onClick={() => navigate(`/dashboard${submodulo.ruta}` || '#')}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                          isActiveRoute(`/dashboard${submodulo.ruta}`)
                            ? 'bg-[#2196F3]/10 text-[#2196F3] border border-[#4CAF50]/20'
                            : 'text-gray-600 hover:bg-[#4CAF50]/10 hover:text-[#2196F3]'
                        }`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full mr-3 ${
                          isActiveRoute(`/dashboard${submodulo.ruta}`) ? 'bg-[#2196F3]' : 'bg-gray-400'
                        }`}></div>
                        <span className="font-medium">{submodulo.nombre}</span>
                      </button>
                    ))
                  }
                </div>
              )}

              {/* Tooltip para modo colapsado */}
              {isCollapsed && (
                <div className="fixed left-20 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 border border-gray-600 ml-2">
                  <div className="font-semibold text-[#4CAF50]">{modulo.nombre}</div>
                  {modulo.descripcion && (
                    <div className="text-xs text-gray-300 mt-1">{modulo.descripcion}</div>
                  )}
                  <div className="absolute top-3 -left-1 w-2 h-2 bg-gray-800 border-l border-b border-gray-600 rotate-45"></div>
                </div>
              )}
            </div>
          );
        })}

      </nav>

      {/* Botón flotante para contraer/expandir sidebar */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute right-2 bottom-20 w-8 h-8 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:bg-gray-50 z-10"
        title={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        )}
      </button>
      
    </aside>
  );
};